import { useId, useState } from 'react';
import { PlusIcon } from '@phosphor-icons/react';
import { Num, T, useGT, useLocale } from 'gt-react';
import type { WorkbookColumnarSheet } from '@erp/shared-types';
import type { ShownDistinctValues } from './sampleDistinctValues';
import type { ColumnType } from '@erp/workbooks';
import { Input } from '@/components/ui/input';
import type { ChangeSheet } from './useWorkbookEditor';
import { workbookCellText } from './workbookCellText';
import {
  EMPTY_ENTRY,
  VALUE_LIST_CHECKBOXES,
  errorsLocked,
  keepsAllValues,
  pickValue,
  searchValueList,
  replaceValueListFilters,
  selectAllValues,
  toggleValueListEntry,
  valueListFilters,
  worksheetValueList,
  type NotePendingFilter,
  type PendingFilter,
  type ValueList,
} from './worksheetValueList';
import { Checkbox } from '@/components/ui/checkbox';

type Filter = WorkbookColumnarSheet['filters'][number];

/**
 * A column's Value list filter as a spreadsheet's filter list: a search box
 * above checkboxes for its values, (Empty) and (Errors). A Field with at most
 * VALUE_LIST_CHECKBOXES Distinct values lists them all and the search narrows
 * them; past that the search suggests values and lists only those picked.
 */
// fallow-ignore-next-line complexity
export function WorksheetValueListFilter({
  sheet,
  column,
  type,
  distinct,
  change,
  notePending,
}: {
  sheet: WorkbookColumnarSheet;
  column: string;
  type: ColumnType;
  /** The column's Distinct values in the shown Result, or a sample of a preview's first rows. */
  distinct?: ShownDistinctValues;
  change: ChangeSheet;
  /** Told of changes that filter rows out, so the grid can hint at them. */
  notePending?: NotePendingFilter;
}) {
  const gt = useGT();
  const locale = useLocale();
  const listId = useId();
  const [search, setSearch] = useState('');
  const [picked, setPicked] = useState<string[]>([]);
  const list = worksheetValueList(sheet.filters, column, distinct, picked);
  const emptyLabel = gt('(Empty)', {
    context: 'worksheet value list empty entry',
  });
  const text = (value: string) =>
    value === EMPTY_ENTRY ? emptyLabel : workbookCellText(value, type, locale);
  function edited(
    filters: readonly Filter[],
    update: (list: ValueList) => ValueList,
    also: string[],
  ): Filter[] | null {
    const current = worksheetValueList(filters, column, distinct, [
      ...picked,
      ...also,
    ]);
    const next = replaceValueListFilters(
      filters,
      column,
      valueListFilters(update(current), column),
    );
    // The schema holds 30 filters at most.
    return next.length > 30 ? null : next;
  }
  function edit(
    update: (list: ValueList) => ValueList,
    also: string[] = [],
    pending?: Omit<PendingFilter, 'column'>,
  ) {
    const after = pending && edited(sheet.filters, update, also);
    if (pending && after) notePending?.({ column, ...pending }, after);
    change((latest) => {
      const filters = edited(latest.filters, update, also);
      return filters ? { ...latest, filters } : latest;
    });
  }
  const entryCell = (value: string) =>
    value === EMPTY_ENTRY
      ? { kind: 'empty' as const }
      : { kind: 'value' as const, value };
  function pick(value: string) {
    setPicked((items) => (items.includes(value) ? items : [...items, value]));
    setSearch('');
    edit(
      (current) => pickValue(current, value),
      [value],
      // Picking on a list that keeps every value keeps only the pick.
      keepsAllValues(list) ? { cell: entryCell(value), keep: true } : undefined,
    );
  }
  const { shown, suggestions, typed, canAddTyped, allChecked } =
    searchValueList(list, distinct, search, text);
  const locked = errorsLocked(list);
  return (
    <div className="space-y-2">
      <Input
        type="search"
        role="combobox"
        aria-expanded={suggestions.length > 0 || canAddTyped}
        aria-controls={listId}
        aria-autocomplete="list"
        className="h-8 text-xs"
        aria-label={gt('Search values of {column}', {
          context: 'worksheet value list search',
          column,
        })}
        placeholder={gt('Search', {
          context: 'worksheet value list search placeholder',
        })}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          const first = suggestions[0]?.value;
          if (first !== undefined) pick(first);
          else if (canAddTyped) pick(typed);
        }}
      />
      {(suggestions.length > 0 || canAddTyped) && (
        <ul
          id={listId}
          role="listbox"
          aria-label={gt('Matching values', {
            context: 'worksheet value list suggestions',
          })}
          className="rounded border text-xs"
        >
          {suggestions.map((entry) => (
            <li key={entry.value} role="option" aria-selected={false}>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-accent"
                onClick={() => pick(entry.value)}
              >
                <span className="min-w-0 flex-1 truncate">
                  {text(entry.value)}
                </span>
                <span className="tabular-nums text-muted-foreground">
                  <Num>{entry.count}</Num>
                </span>
              </button>
            </li>
          ))}
          {canAddTyped && (
            <li role="option" aria-selected={false}>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-2 py-1 text-left hover:bg-accent"
                onClick={() => pick(typed)}
              >
                <PlusIcon size={12} aria-hidden="true" />
                {gt('Add "{value}"', {
                  context: 'worksheet value list add typed value',
                  value: typed,
                })}
              </button>
            </li>
          )}
        </ul>
      )}
      <div
        role="group"
        aria-label={gt('Values of {column}', {
          context: 'worksheet value list',
          column,
        })}
        className="max-h-56 space-y-0.5 overflow-auto rounded border p-1 text-xs"
      >
        {(list.entries.length > 0 || list.errors !== null) && (
          <ValueCheckbox
            label={gt('(Select all)', {
              context: 'worksheet value list select all',
            })}
            checked={allChecked}
            onChange={() =>
              edit((current) => selectAllValues(current, !allChecked))
            }
          />
        )}
        {shown.map((entry) => (
          <ValueCheckbox
            key={entry.value}
            label={text(entry.value)}
            count={entry.count}
            checked={entry.checked}
            onChange={() =>
              edit(
                (current) => toggleValueListEntry(current, entry.value),
                [],
                entry.checked
                  ? { cell: entryCell(entry.value), keep: false }
                  : undefined,
              )
            }
          />
        ))}
        {list.errors !== null && (
          <ValueCheckbox
            label={gt('(Errors)', {
              context: 'worksheet value list error entry',
            })}
            count={distinct?.errorCount || undefined}
            checked={list.errors}
            disabled={locked}
            title={
              locked
                ? gt(
                    'A value list never keeps errors. Select all values to keep them.',
                    { context: 'worksheet value list errors locked' },
                  )
                : undefined
            }
            onChange={() =>
              edit(
                (current) => ({ ...current, errors: !current.errors }),
                [],
                list.errors
                  ? { cell: { kind: 'errors' }, keep: false }
                  : undefined,
              )
            }
          />
        )}
        {!list.entries.length && list.errors === null && (
          <p className="p-1 text-muted-foreground">
            {distinct ? (
              <T context="worksheet value list with too many values to show">
                More than <Num>{VALUE_LIST_CHECKBOXES}</Num> values. Search to
                pick the ones to keep.
              </T>
            ) : (
              <T context="worksheet value list without a run">
                Run to see values. You can still type one above.
              </T>
            )}
          </p>
        )}
      </div>
      <ValueListNote list={list} distinct={distinct} />
    </div>
  );
}

/** What a value list's entries are: a sample, only those picked, or none known before a Run. */
function ValueListNote({
  list,
  distinct,
}: {
  list: ValueList;
  distinct?: ShownDistinctValues;
}) {
  if (distinct?.sampleRows !== undefined)
    return <SampleValuesNote rows={distinct.sampleRows} />;
  if (!list.entries.length || list.complete) return null;
  return (
    <p className="text-xs text-muted-foreground">
      {distinct ? (
        <T context="worksheet value list showing only picked values">
          Showing only the values you picked. Search to add more.
        </T>
      ) : (
        <T context="worksheet value list without a run">
          Run to see values. You can still type one above.
        </T>
      )}
    </p>
  );
}

/**
 * Says a value list is a sample: counted from a preview's first rows, since a
 * Run didn't read the column. Its counts are of those rows only.
 */
export function SampleValuesNote({ rows }: { rows: number }) {
  return (
    <p className="text-xs text-muted-foreground">
      <T context="filter value list counted from a data preview's first rows">
        From the first <Num>{rows}</Num> rows
      </T>
    </p>
  );
}

function ValueCheckbox({
  label,
  count,
  checked,
  disabled,
  title,
  onChange,
}: {
  label: string;
  count?: number;
  checked: boolean;
  disabled?: boolean;
  title?: string;
  onChange: () => void;
}) {
  return (
    <label
      className="flex cursor-pointer items-center gap-2 rounded px-1 py-0.5 hover:bg-accent has-[:disabled]:cursor-default has-[:disabled]:opacity-60"
      title={title}
    >
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={onChange}
      />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {count !== undefined && (
        <span className="tabular-nums text-muted-foreground">
          <Num>{count}</Num>
        </span>
      )}
    </label>
  );
}
